import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Form,
  FormItem,
  InputGroup,
} from "@clarkinc/zest-design-system/ui";
import { useForm, SubmitHandler } from "react-hook-form";
import debounce from "lodash/debounce";
import connectUser, { User } from "../Store/connectUserAndChat";
import { sendCustomerIoNewsletterSignupEvent } from "~/analytics/CustomerIo/EmailEvents";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

type FormState = {
  emailaddress: string;
};

const validationSchema = z.object({
  emailaddress: z.string().trim().email("Please enter a valid email address."),
});

const MailingList = ({ user }: { user: User }) => {
  const [showMessage, setShowMessage] = useState(false);
  const [errorSubmitting, setErrorSubmitting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [alertWidth, setAlertWidth] = useState<number | undefined>(
    window.innerWidth > 768 ? undefined : window.innerWidth - 84,
  );

  const form = useForm<FormState>({
    resolver: zodResolver(validationSchema),
    defaultValues: { emailaddress: "" },
  });

  const emailAddress = form.watch("emailaddress");

  const {
    formState: { errors },
    reset,
  } = form;

  const handleClose = () => {
    setShowMessage(false);
  };

  const handleSubmit: SubmitHandler<FormState> = async ({ emailaddress }) => {
    setIsSubmitting(true);

    try {
      const body = new FormData();
      body.append("emailaddress", emailaddress);

      const response = await fetch(`/api/subscribetoallemails/`, {
        method: "POST",
        body,
      });

      if (!response.ok) {
        setErrorSubmitting(true);
        return;
      }

      const { emailSignupEvent } = await import("~/gtm");
      emailSignupEvent(emailaddress);
      sendCustomerIoNewsletterSignupEvent(emailaddress);

      setErrorSubmitting(false);
      reset();
      setShowMessage(true);
    } catch {
      setErrorSubmitting(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setAlertWidth(window.innerWidth - 84);
      } else {
        setAlertWidth(undefined);
      }
    };

    window.addEventListener("resize", debounce(handleResize, 200));

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      className={`!flex border-solid border-0 border-b box-border items-center border-gray-300 md:border-0 lg:border-r md:w-full px-4 md:px-0 lg:max-w-none lg:pr-[32px] lz:pl-2.5 xl:pl-0 py-6 lg:h-[139px] xl:h-[119px] ${
        user.hasWebPlus ? "md:max-w-[638px]" : "md:max-w-[580px]"
      }`}
    >
      {showMessage ? (
        !errorSubmitting && (
          <Alert
            theme="wss"
            isOpen
            variant="success"
            closable
            onClose={handleClose}
            style={{ width: alertWidth }}
            closeButtonProps={{ className: "bg-transparent" }}
          >
            <p className="m-0 text-gray-800 text-base leading-5 !flex flex-col md:flex-row lg:flex-col lz:flex-row">
              Thanks for Joining! &nbsp;
              <a
                className="text-gray-800"
                href="/blog/2150/monthly-coupon-code-update.html"
              >
                Shop our current sales
              </a>
            </p>
          </Alert>
        )
      ) : (
        <Form
          aria-label="email sign up"
          form={form}
          data-testid="email-input-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="!flex w-full h-full items-center justify-center"
        >
          <FormItem name="emailaddress">
            <InputGroup
              theme="wss"
              id="emailaddress"
              aria-label="Enter your email to get latest deals & more!"
              groupLabel="Enter your email to get latest deals & more!"
            >
              <div className="flex flex-col items-start md:flex-row md:items-center md:gap-x-[26px] lg:flex-col lg:items-start lg:gap-x-1 xl:flex-row xl:items-center xl:gap-x-[26px]">
                <InputGroup.Label className="mb-0.5 text-xl !leading-5 text-left md:mb-0 lg:mb-0.5 xl:mb-0 lg:text-lg xl:text-xl">
                  Enter your email to get latest deals & more!
                </InputGroup.Label>
                <InputGroup.Wrapper className="w-full">
                  <div className="relative flex flex-col w-full">
                    <InputGroup.Wrapper>
                      <InputGroup.Input
                        theme="wss"
                        type="email"
                        inputSize="large"
                        name="emailaddress"
                        data-testid="footer-email-input"
                        value={emailAddress}
                        onValueChange={(value) =>
                          form.setValue("emailaddress", value)
                        }
                        hasError={!!errors.emailaddress}
                      />
                      <Button
                        data-testid="email-signup-button"
                        className="whitespace-nowrap px-3-1/2"
                        size="large"
                        type="submit"
                        color="blue"
                        disabled={isSubmitting}
                      >
                        Sign Up
                      </Button>
                    </InputGroup.Wrapper>
                    <div className="absolute -bottom-[60%] w-full">
                      <InputGroup.Error
                        errorMessages={errors.emailaddress?.message}
                      />
                    </div>
                  </div>
                </InputGroup.Wrapper>
              </div>
            </InputGroup>
          </FormItem>
        </Form>
      )}
    </div>
  );
};

export default connectUser(MailingList);
